import React from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown } from 'antd';
import { GlobalOutlined, CheckOutlined } from '@ant-design/icons';
import { useAuth } from '../../context/AuthContext';
import {
  normalizeLanguageCode,
  setGuestLanguagePreference,
} from '../../utils/languagePreference';
import styles from './LanguageSwitcher.module.css';

/**
 * Language options with flag emojis and native names
 */
const languages = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
];

/**
 * LanguageSwitcher Component
 * A clean, compact language selector for the header
 * Uses Ant Design Dropdown with custom styling
 */
const LanguageSwitcher = () => {
  const { i18n } = useTranslation();
  const { user, hasConfirmedSession } = useAuth();

  const currentLanguageCode = normalizeLanguageCode(i18n.resolvedLanguage || i18n.language) || languages[0].code;
  const currentLanguage = languages.find(lang => lang.code === currentLanguageCode) || languages[0];

  const handleLanguageChange = (langCode) => {
    if (!user && !hasConfirmedSession) {
      setGuestLanguagePreference(langCode);
    }

    i18n.changeLanguage(langCode);
  };

  const menuItems = languages.map(lang => {
    const isActive = currentLanguageCode === lang.code;
    return {
      key: lang.code,
      label: (
        <div 
          className={`${styles.menuItem} ${isActive ? styles.active : ''}`}
          onClick={() => handleLanguageChange(lang.code)}
        >
          <span className={styles.flag}>{lang.flag}</span>
          <div className={styles.langInfo}>
            <span className={styles.langName}>{lang.name}</span>
            <span className={styles.langCode}>{lang.code.toUpperCase()}</span>
          </div>
          <span className={styles.checkmark}>
            {isActive && <CheckOutlined />}
          </span>
        </div>
      ),
    };
  });

  return (
    <Dropdown
      menu={{ items: menuItems }}
      trigger={['click']}
      placement="bottomRight"
      overlayClassName={styles.dropdownOverlay}
    >
      <button className={styles.switcherButton} aria-label="Select language">
        <GlobalOutlined className={styles.globeIcon} />
        <span className={styles.currentLang}>{currentLanguage.code.toUpperCase()}</span>
      </button>
    </Dropdown>
  );
};

export default LanguageSwitcher;
